import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  alpha,
  useTheme,
  Skeleton,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Divider,
  Menu,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Business,
  Refresh,
  Payment,
  Receipt,
  Download,
  TrendingUp,
  Warning,
  CheckCircle,
  Cancel,
  AccessTime,
  MoreVert,
  Pause,
  PlayArrow,
  SwapHoriz,
  History,
  Add,
  CreditCard,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format, differenceInDays } from 'date-fns';
import subscriptionService, {
  Subscription,
  Plan,
  Payment as PaymentType,
  SubscriptionStats,
  SubscriptionHistoryItem,
} from '../../services/subscriptionService';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import { useTimezone } from '../../hooks/useTimezone';
import { toUploadUrl } from '../../utils/uploadUrl';
import { usePlatformInvoicePdf } from '../../components/billing/usePlatformInvoicePdf';

// School logos are stored in the DB as bare filenames (e.g.
// `logos-17712638749793-…svg`) — *not* paths. Single-arg toUploadUrl would
// build `<host>/logos-…svg` which 404s. Route bare names through the two-arg
// form so they hit `/uploads/logos/<file>`. Same helper used on the Schools
// page so the avatar looks identical here.
const resolveSchoolLogo = (logo?: string | null): string | undefined => {
  if (!logo) return undefined;
  if (/^https?:\/\//i.test(logo)) return logo;
  if (logo.startsWith('/')) return toUploadUrl(logo);
  return toUploadUrl('logos', logo);
};

const schoolInitials = (name?: string): string => {
  if (!name) return '?';
  return name.split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';
};

const SubscriptionsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { formatDate, formatDateTime } = useTimezone();

  // State
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true); // Track initial load vs subsequent searches
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // Debounce to prevent API spam
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Action Menu
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

  // Change Plan dialog
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [changePlanForm, setChangePlanForm] = useState({
    planId: '',
    billingCycle: 'monthly' as 'monthly' | 'yearly',
  });

  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    paymentMethod: 'bank_transfer' as 'bank_transfer' | 'cash' | 'cheque' | 'online',
    paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '',
    notes: '',
    extendSubscription: true,
  });

  // Payment History dialog
  const [paymentHistoryDialogOpen, setPaymentHistoryDialogOpen] = useState(false);
  const [payments, setPayments] = useState<PaymentType[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const invoices = usePlatformInvoicePdf();
  const downloadingInvoiceId = invoices.busyId;

  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [history, setHistory] = useState<SubscriptionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Suspend/Resume confirmation
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);

  // Fetch subscriptions only (used for search/filter)
  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const subsResponse = await subscriptionService.getAllSubscriptions({
        status: statusFilter || undefined,
        search: debouncedSearchTerm || undefined,
        page,
        limit: rowsPerPage,
      });
      setSubscriptions(subsResponse.data);
      setTotalCount(subsResponse.pagination.total);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load subscriptions', { variant: 'error' });
    } finally {
      setLoading(false);
      if (initialLoad) {
        setInitialLoad(false);
      }
    }
  }, [statusFilter, debouncedSearchTerm, page, rowsPerPage, enqueueSnackbar, initialLoad]);

  // Fetch plans and stats only once on mount
  const fetchPlansAndStats = useCallback(async () => {
    try {
      const [plansData, statsData] = await Promise.all([
        subscriptionService.getAllPlans(),
        subscriptionService.getSubscriptionStats(),
      ]);
      setPlans(plansData.filter((p) => p.isActive));
      setStats(statsData);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load plans/stats', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Load plans and stats once on mount
  useEffect(() => {
    fetchPlansAndStats();
  }, [fetchPlansAndStats]);

  // Load subscriptions when filters change
  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Fetch payments for a subscription
  const fetchPayments = async (schoolId: string) => {
    setPaymentsLoading(true);
    try {
      const response = await subscriptionService.getPaymentsBySchool(schoolId);
      setPayments(response.data);
    } catch (error: any) {
      enqueueSnackbar('Failed to load payments', { variant: 'error' });
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Fetch subscription history
  const fetchHistory = async (schoolId: string) => {
    setHistoryLoading(true);
    try {
      const response = await subscriptionService.getSubscriptionHistory(schoolId);
      setHistory(response.data);
    } catch (error: any) {
      enqueueSnackbar('Failed to load history', { variant: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handlers
  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, subscription: Subscription) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedSubscription(subscription);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  const handleOpenPayment = async () => {
    if (!selectedSubscription) return;
    handleActionMenuClose();
    setPaymentForm({
      amount: 0,
      paymentMethod: 'bank_transfer',
      paymentDate: new Date().toISOString().split('T')[0],
      referenceNumber: '',
      notes: '',
      extendSubscription: true,
    });
    const school = selectedSubscription.schoolId as any;
    const plan = selectedSubscription.planId as any;
    if (plan) {
      setPaymentForm((prev) => ({
        ...prev,
        amount:
          selectedSubscription.billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice,
      }));
    }
    setPaymentDialogOpen(true);
  };

  const handleOpenPaymentHistory = async () => {
    if (!selectedSubscription) return;
    handleActionMenuClose();
    const school = selectedSubscription.schoolId as any;
    await fetchPayments(school._id);
    setPaymentHistoryDialogOpen(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedSubscription) return;
    const school = selectedSubscription.schoolId as any;

    try {
      await subscriptionService.recordPayment({
        schoolId: school._id,
        subscriptionId: selectedSubscription._id,
        amount: paymentForm.amount,
        billingCycle: selectedSubscription.billingCycle,
        paymentMethod: paymentForm.paymentMethod,
        paymentDate: paymentForm.paymentDate,
        referenceNumber: paymentForm.referenceNumber,
        notes: paymentForm.notes,
        extendSubscription: paymentForm.extendSubscription,
      });
      enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
      setPaymentDialogOpen(false);
      fetchSubscriptions();
      fetchPlansAndStats(); // Also refresh stats after payment
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to record payment', {
        variant: 'error',
      });
    }
  };

  // The invoice is drawn in the browser; the button spins while it does.
  const handleDownloadInvoice = (payment: PaymentType) =>
    invoices.download(payment._id, payment.invoiceNumber, () => subscriptionService.getInvoice(payment._id));


  const handleOpenChangePlan = () => {
    if (!selectedSubscription) return;
    handleActionMenuClose();
    const plan = selectedSubscription.planId as any;
    setChangePlanForm({
      planId: plan?._id || '',
      billingCycle: selectedSubscription.billingCycle,
    });
    setChangePlanDialogOpen(true);
  };

  const handleChangePlan = async () => {
    if (!selectedSubscription) return;

    try {
      await subscriptionService.updateSubscription(selectedSubscription._id, {
        planId: changePlanForm.planId,
        billingCycle: changePlanForm.billingCycle,
      } as any);
      enqueueSnackbar('Subscription plan updated successfully', { variant: 'success' });
      setChangePlanDialogOpen(false);
      fetchSubscriptions();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to update plan', {
        variant: 'error',
      });
    }
  };

  const handleOpenHistory = async () => {
    if (!selectedSubscription) return;
    handleActionMenuClose();
    const school = selectedSubscription.schoolId as any;
    await fetchHistory(school._id);
    setHistoryDialogOpen(true);
  };

  const handleSuspend = async () => {
    if (!selectedSubscription) return;

    try {
      await subscriptionService.suspendSubscription(selectedSubscription._id, suspendReason);
      enqueueSnackbar('Subscription suspended successfully', { variant: 'success' });
      setSuspendDialogOpen(false);
      setSuspendReason('');
      fetchSubscriptions();
      fetchPlansAndStats(); // Also refresh stats
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to suspend subscription', {
        variant: 'error',
      });
    }
  };

  const handleResume = async () => {
    if (!selectedSubscription) return;

    try {
      await subscriptionService.resumeSubscription(selectedSubscription._id);
      enqueueSnackbar('Subscription resumed successfully', { variant: 'success' });
      setResumeDialogOpen(false);
      fetchSubscriptions();
      fetchPlansAndStats(); // Also refresh stats
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to resume subscription', {
        variant: 'error',
      });
    }
  };

  // Status chip render
  const renderStatusChip = (status: string) => {
    const config: Record<string, { color: string; icon: React.ReactNode; label?: string }> = {
      active: {
        color: theme.palette.success.main,
        icon: <CheckCircle sx={{ fontSize: 14 }} />,
      },
      trial: { color: theme.palette.info.main, icon: <AccessTime sx={{ fontSize: 14 }} /> },
      expired: { color: theme.palette.error.main, icon: <Warning sx={{ fontSize: 14 }} /> },
      cancelled: { color: theme.palette.grey[500], icon: <Cancel sx={{ fontSize: 14 }} /> },
      suspended: {
        color: theme.palette.warning.main,
        icon: <Pause sx={{ fontSize: 14 }} />,
      },
      pending_payment: {
        color: theme.palette.warning.dark,
        icon: <Payment sx={{ fontSize: 14 }} />,
        label: 'Pending Payment',
      },
    };
    const { color, icon, label } = config[status] || config.active;

    return (
      <Chip
        icon={icon as any}
        label={label || (status.charAt(0).toUpperCase() + status.slice(1))}
        size="small"
        sx={{
          bgcolor: alpha(color, 0.1),
          color: color,
          fontWeight: 600,
          '& .MuiChip-icon': { color: color },
        }}
      />
    );
  };

  // Days until expiry
  const getDaysUntilExpiry = (endDate: string) => {
    const days = differenceInDays(new Date(endDate), new Date());
    return days;
  };

  // Get action label based on history action
  const getHistoryActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      created: 'Subscription Created',
      plan_changed: 'Plan Changed',
      billing_cycle_changed: 'Billing Cycle Changed',
      extended: 'Subscription Extended',
      suspended: 'Subscription Suspended',
      resumed: 'Subscription Resumed',
      cancelled: 'Subscription Cancelled',
      expired: 'Subscription Expired',
      renewed: 'Subscription Renewed',
    };
    return labels[action] || action;
  };

  // Get action color based on history action
  const getHistoryActionColor = (action: string) => {
    const colors: Record<string, string> = {
      created: theme.palette.success.main,
      plan_changed: theme.palette.info.main,
      billing_cycle_changed: theme.palette.info.main,
      extended: theme.palette.success.main,
      suspended: theme.palette.warning.main,
      resumed: theme.palette.success.main,
      cancelled: theme.palette.error.main,
      expired: theme.palette.error.main,
      renewed: theme.palette.success.main,
    };
    return colors[action] || theme.palette.grey[500];
  };

  // Loading skeleton - ONLY show on initial load, not during searches
  if (initialLoad && loading) {
    return (
      <Box>
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 2 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Manage Subscriptions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage school subscriptions
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            fetchSubscriptions();
            fetchPlansAndStats();
          }}
          sx={{ borderRadius: 2 }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <Business sx={{ color: theme.palette.primary.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {stats.totalSubscriptions}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Subscriptions
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    <CheckCircle sx={{ color: theme.palette.success.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {stats.statusBreakdown?.active || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                    <Warning sx={{ color: theme.palette.warning.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {stats.expiringSoon}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Expiring Soon
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    <TrendingUp sx={{ color: theme.palette.success.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      PKR {(stats.monthlyRevenue || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Revenue (This Month)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by school name or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Subscriptions Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Loading overlay for searches */}
        {loading && !initialLoad && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.7),
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>School</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Billing</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Validity</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                    <Business sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm || statusFilter
                        ? 'No subscriptions match your filters'
                        : 'No subscriptions found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map((subscription) => {
                  const school = subscription.schoolId as any;
                  const plan = subscription.planId as any;
                  const daysLeft = getDaysUntilExpiry(subscription.endDate);

                  return (
                    <TableRow key={subscription._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={resolveSchoolLogo(school?.logo)}
                            variant="rounded"
                            sx={{
                              width: 36, height: 36,
                              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.1),
                              color: 'primary.main',
                              fontSize: 14, fontWeight: 700,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`
                            }}
                          >
                            {schoolInitials(school?.name)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {school?.name || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {school?.email || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {plan?.name || 'No Plan'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            subscription.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{renderStatusChip(subscription.status)}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {formatDate(subscription.endDate, 'MMM dd, yyyy')}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              subscription.status === 'pending_payment'
                                ? 'warning.main'
                                : daysLeft <= 7
                                ? 'error.main'
                                : daysLeft <= 30
                                ? 'warning.main'
                                : 'text.secondary'
                            }
                          >
                            {subscription.status === 'pending_payment'
                              ? 'Awaiting Payment'
                              : daysLeft > 0 
                              ? `${daysLeft} days left` 
                              : 'Expired'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="More Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, subscription)}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 200,
            mt: 0.5,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
              gap: 1.5,
            },
          },
        }}
      >
        <MenuItem onClick={handleOpenPayment}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CreditCard fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Record Payment"
            secondary="Extend subscription"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </MenuItem>

        <MenuItem onClick={handleOpenChangePlan}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SwapHoriz fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Change Plan"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          />
        </MenuItem>

        <MenuItem onClick={handleOpenPaymentHistory}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Receipt fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            primary="Payment History"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          />
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {selectedSubscription?.status === 'suspended' ? (
          <MenuItem
            onClick={() => {
              handleActionMenuClose();
              setResumeDialogOpen(true);
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PlayArrow fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="Resume"
              secondary="Restore access"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleActionMenuClose();
              setSuspendDialogOpen(true);
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Pause fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText
              primary="Suspend"
              secondary="Temporarily block access"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        )}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={handleOpenHistory}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <History fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            primary="View History"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          />
        </MenuItem>
      </Menu>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
              <CreditCard sx={{ fontSize: 20, color: theme.palette.success.main }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} fontSize="1.1rem">
                Record Payment
              </Typography>
              {selectedSubscription && (
                <Typography variant="caption" color="text.secondary">
                  {(selectedSubscription.schoolId as any)?.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount (PKR)"
                  value={paymentForm.amount}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      amount: parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentForm.paymentMethod}
                    label="Payment Method"
                    onChange={(e) =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        paymentMethod: e.target.value as any,
                      }))
                    }
                  >
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="cheque">Cheque</MenuItem>
                    <MenuItem value="online">Online</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Payment Date"
                  value={paymentForm.paymentDate}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, paymentDate: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Reference Number"
                  value={paymentForm.referenceNumber}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, referenceNumber: e.target.value }))
                  }
                  placeholder="Cheque #, Bank ref, etc."
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={paymentForm.notes}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <input
                      type="checkbox"
                      checked={paymentForm.extendSubscription}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          extendSubscription: e.target.checked,
                        }))
                      }
                      style={{ width: 18, height: 18 }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Extend subscription by 1{' '}
                        {selectedSubscription?.billingCycle === 'yearly' ? 'year' : 'month'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Automatically add time to the subscription upon payment
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button onClick={() => setPaymentDialogOpen(false)} color="inherit" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleRecordPayment}
            variant="contained"
            color="success"
            startIcon={<Payment />}
            size="small"
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog
        open={paymentHistoryDialogOpen}
        onClose={() => setPaymentHistoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
              <Receipt sx={{ fontSize: 20, color: theme.palette.info.main }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} fontSize="1.1rem">
                Payment History
              </Typography>
              {selectedSubscription && (
                <Typography variant="caption" color="text.secondary">
                  {(selectedSubscription.schoolId as any)?.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {paymentsLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Skeleton variant="text" width="100%" height={40} />
              <Skeleton variant="text" width="100%" height={40} />
              <Skeleton variant="text" width="100%" height={40} />
            </Box>
          ) : payments.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Receipt sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No payment history</Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {payments.map((payment, index) => (
                <React.Fragment key={payment._id}>
                  <ListItem sx={{ py: 1.5, px: 2.5, gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" fontWeight={600}>
                          PKR {payment.amount.toLocaleString()}
                        </Typography>
                        <Chip
                          label={payment.paymentMethod.replace('_', ' ').toUpperCase()}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {payment.invoiceNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(payment.paymentDate, 'MMM dd, yyyy')}
                        {payment.referenceNumber && ` • Ref: ${payment.referenceNumber}`}
                      </Typography>
                    </Box>
                    <Tooltip title={downloadingInvoiceId === payment._id ? "Downloading..." : "Download Invoice"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadInvoice(payment)}
                          disabled={downloadingInvoiceId === payment._id}
                          sx={{ alignSelf: 'center' }}
                        >
                          {downloadingInvoiceId === payment._id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Download fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ListItem>
                  {index < payments.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setPaymentHistoryDialogOpen(false)} size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog
        open={changePlanDialogOpen}
        onClose={() => setChangePlanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle component="div">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <SwapHoriz sx={{ color: theme.palette.primary.main }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Change Subscription Plan
              </Typography>
              {selectedSubscription && (
                <Typography variant="body2" color="text.secondary">
                  {(selectedSubscription.schoolId as any)?.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={changePlanForm.planId}
                  label="Plan"
                  onChange={(e) =>
                    setChangePlanForm((prev) => ({ ...prev, planId: e.target.value }))
                  }
                >
                  {plans.map((plan) => (
                    <MenuItem key={plan._id} value={plan._id}>
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {plan.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PKR {plan.monthlyPrice.toLocaleString()}/mo • PKR{' '}
                          {plan.yearlyPrice.toLocaleString()}/yr
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Billing Cycle</InputLabel>
                <Select
                  value={changePlanForm.billingCycle}
                  label="Billing Cycle"
                  onChange={(e) =>
                    setChangePlanForm((prev) => ({
                      ...prev,
                      billingCycle: e.target.value as any,
                    }))
                  }
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly (Save money)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setChangePlanDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleChangePlan} variant="contained" startIcon={<SwapHoriz />}>
            Update Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
              <History sx={{ fontSize: 20, color: theme.palette.info.main }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} fontSize="1.1rem">
                Subscription History
              </Typography>
              {selectedSubscription && (
                <Typography variant="caption" color="text.secondary">
                  {(selectedSubscription.schoolId as any)?.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {historyLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Skeleton variant="text" width="100%" height={40} />
              <Skeleton variant="text" width="100%" height={40} />
              <Skeleton variant="text" width="100%" height={40} />
            </Box>
          ) : history.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <History sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No history available</Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {history.map((item, index) => (
                <React.Fragment key={item._id}>
                  <ListItem sx={{ py: 1.5, px: 2.5, gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: alpha(getHistoryActionColor(item.action), 0.1),
                      }}
                    >
                      {item.action === 'extended' || item.action === 'renewed' ? (
                        <Payment sx={{ fontSize: 18, color: getHistoryActionColor(item.action) }} />
                      ) : item.action === 'suspended' ? (
                        <Pause sx={{ fontSize: 18, color: getHistoryActionColor(item.action) }} />
                      ) : item.action === 'resumed' ? (
                        <PlayArrow sx={{ fontSize: 18, color: getHistoryActionColor(item.action) }} />
                      ) : (
                        <CheckCircle sx={{ fontSize: 18, color: getHistoryActionColor(item.action) }} />
                      )}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {getHistoryActionLabel(item.action)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatDateTime(item.createdAt, 'MMM dd, yyyy • hh:mm a')}
                      </Typography>
                      {item.previousPlanId && item.newPlanId && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {item.previousPlanId.name} → {item.newPlanId.name}
                        </Typography>
                      )}
                      {item.notes && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {item.notes}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        By {item.performedBy.name}
                      </Typography>
                    </Box>
                  </ListItem>
                  {index < history.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setHistoryDialogOpen(false)} size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Dialog */}
      <ConfirmationDialog
        open={suspendDialogOpen}
        title="Suspend Subscription"
        message="Are you sure you want to suspend this subscription? The school will lose access to all modules immediately."
        onConfirm={handleSuspend}
        onClose={() => {
          setSuspendDialogOpen(false);
          setSuspendReason('');
          handleActionMenuClose();
        }}
        confirmText="Suspend"
        confirmColor="warning"
      >
        <TextField
          fullWidth
          label="Reason (Optional)"
          value={suspendReason}
          onChange={(e) => setSuspendReason(e.target.value)}
          multiline
          rows={2}
          sx={{ mt: 2 }}
        />
      </ConfirmationDialog>

      {/* Resume Dialog */}
      <ConfirmationDialog
        open={resumeDialogOpen}
        title="Resume Subscription"
        message="Are you sure you want to resume this subscription? The school will regain access to all modules."
        onConfirm={handleResume}
        onClose={() => {
          setResumeDialogOpen(false);
          handleActionMenuClose();
        }}
        confirmText="Resume"
        confirmColor="success"
      />
    </Box>
  );
};

export default SubscriptionsPage;
